import React from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Typography, Box, Grid, Divider, IconButton } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder"; // Vitals
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"; // Personal Info
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Additional Info
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"; // Other Details
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined"; // Medical History

const View: React.FC<any> = ({ data, setOpen }) => {
  const sectionStyle = { mb: 1.5 };
  const headingStyle = {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#1976d2",
    mb: 0.5,
    display: "flex",
    alignItems: "center",
    gap: 1,
  };
  const textStyle = { fontSize: "12px", color: "textSecondary" };

  const formatLabel = (key: string) =>
    key.replace(/([A-Z])/g, " $1").toUpperCase();

  const vitalUnits = {
    bloodPressure: " mmHg",
    height: " cm",
    pulse: " bpm",
    temperature: " °C",
    weight: " kg",
  };

  return (
    <Box
      sx={{
        padding: 2,
        width: "100%",
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      {/* Header with Close Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ fontSize: "16px", color: "#333" }}
        >
          Donor Details
        </Typography>
        <IconButton
          onClick={() => setOpen(false)}
          sx={{ color: "gray", "&:hover": { color: "red" } }}
        >
          <CancelOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Vitals Section */}
      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <FavoriteBorderIcon sx={{ fontSize: 18 }} />
          Vitals
        </Typography>
        <Divider />
        <Grid container spacing={1}>
          {["bloodPressure", "height", "pulse", "temperature", "weight", "bloodGroup"].map(
            (key) => (
              <Grid item xs={6} sm={3} key={key}>
                <Typography sx={textStyle}>
                  <strong>{formatLabel(key)}:</strong>{" "}
                  {data.vitals?.[key] || "N/A"}
                  {vitalUnits[key as keyof typeof vitalUnits] || ""}
                </Typography>
              </Grid>
            )
          )}
        </Grid>
      </Box>

      {/* Personal Information Section */}
      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <PersonOutlineIcon sx={{ fontSize: 18 }} />
          Personal Information
        </Typography>
        <Divider />
        <Grid container spacing={1}>
          {["name", "age", "gender", "city"].map((key) => (
            <Grid item xs={6} sm={3} key={key}>
              <Typography sx={textStyle}>
                <strong>
                  {key === "city" ? "City/Address" : formatLabel(key)}:
                </strong>{" "}
                {data[key] || "N/A"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Additional Information Section */}
      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          Additional Information
        </Typography>
        <Divider />
        <Grid container spacing={1}>
          {["cellNo", "cnic", "occupation"].map((key) => (
            <Grid item xs={6} sm={3} key={key}>
              <Typography sx={textStyle}>
                <strong>{formatLabel(key)}:</strong> {data[key] || "N/A"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Medical History Section */}
      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <MedicalServicesOutlinedIcon sx={{ fontSize: 18 }} />
          Medical History
        </Typography>
        <Divider />
        <Grid container spacing={1}>
          {data.medicalHistory?.length > 0 ? (
            data.medicalHistory.map((item: any, index: number) => (
              <Grid item xs={12} md={6} key={index}>
                <Typography sx={textStyle}>
                  <strong>{item.question}:</strong>{" "}
                  {item.answer ? "Yes" : "No"}
                </Typography>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography sx={textStyle}>No medical history available</Typography>
            </Grid>
          )}
        </Grid>
        <Typography sx={{ ...textStyle, mt: 1 }}>
          <strong>Remarks:</strong> {data.remarks || "Fit"}
        </Typography>
      </Box>

      {/* Other Details Section */}
      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <MoreHorizIcon sx={{ fontSize: 18 }} />
          Other Details
        </Typography>
        <Divider />
        <Grid container spacing={1}>
          {[
            "area",
            "anyOtherInformation",
            "lastDonationDate",
            "centerOfDonation",
            "donationType",
            "hemoglobin",
            "generalPhysicalAppearance",
          ].map((key) => (
            <Grid item xs={6} sm={3} key={key}>
              <Typography sx={textStyle}>
                <strong>{formatLabel(key)}:</strong> {data[key] || "N/A"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={sectionStyle}>
        <Typography sx={headingStyle}>
          <MoreHorizIcon sx={{ fontSize: 18 }} />
          Signature / Thumb
        </Typography>
        <Divider />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {[
            { label: "Donor Signature/Thumb", value: data?.donorSignature },
            { label: "Doctor Signature/Thumb", value: data?.doctorSignature },
          ].map(item => (
            <Grid item xs={6} key={item.label}>
              <Typography sx={{ ...textStyle, fontWeight: 700, mb: 0.5 }}>
                {item.label}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #d0d0d0",
                  borderRadius: "4px",
                  height: 110,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#fafafa",
                }}
              >
                {String(item.value || "").startsWith("data:image/") ? (
                  <Box
                    component="img"
                    src={item.value}
                    alt={item.label}
                    sx={{ maxWidth: "100%", maxHeight: 100, objectFit: "contain" }}
                  />
                ) : (
                  <Typography sx={textStyle}>N/A</Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default View;